import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { Dialog, DialogContent, DialogTitle } from '@/components/shadcn/dialog';
import { invoiceService } from '@/services/invoiceService';
import { formatRupiah } from '@/utils/cashflowUtils';
import showToast from '@/components/ui/Toast';

interface ReceivePaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: any | null;
  arenaSlug: string;
  onSuccess: (updatedInvoiceNumber: string, method: string) => void;
}

const PAYMENT_METHODS = [
  {
    id: 'Cash',
    title: 'Cash',
    subtitle: 'Walk-in: bayar tunai di lapangan',
    icon: 'ph:money-bold',
  },
  {
    id: 'Transfer',
    title: 'Transfer',
    subtitle: 'Walk-in: Transfer ke rekening arena',
    icon: 'ph:bank-bold',
  },
  {
    id: 'QRIS',
    title: 'QRIS',
    subtitle: 'App: tampilkan kode QR untuk discan pelanggan',
    icon: 'ph:qr-code-bold',
  },
];

export const ReceivePaymentModal: React.FC<ReceivePaymentModalProps> = ({
  isOpen,
  onClose,
  invoice,
  arenaSlug,
  onSuccess,
}) => {
  const [submittingMethod, setSubmittingMethod] = useState<string | null>(null);

  if (!isOpen || !invoice) return null;

  const rawAmt = invoice.amount || invoice.subtotal || invoice.total_amount || 0;
  const rawStatus = (invoice.payment_status || '').toLowerCase();
  const rawPaymentType = (invoice.payment_type || '').toLowerCase();
  const isDP = rawStatus === 'partial' || rawStatus.includes('dp') || rawPaymentType === 'dp' || rawPaymentType === 'dp_balance';

  // Calculate remaining amount
  const remainingAmount = typeof invoice.remaining_amount === 'number' && invoice.remaining_amount > 0
    ? invoice.remaining_amount
    : (isDP ? (invoice.total_amount && invoice.paid_amount ? invoice.total_amount - invoice.paid_amount : rawAmt) : rawAmt);

  // Subtitle info
  const customerName = invoice.customer_name || 'Pelanggan';
  const productInfo = invoice.product || 'Booking Lapangan';

  // Format date info for subtitle
  let dateSubtitle = '';
  if (invoice.transaction_date) {
    try {
      const d = new Date(invoice.transaction_date);
      if (!isNaN(d.getTime())) {
        const hours = String(d.getHours()).padStart(2, '0');
        const mins = String(d.getMinutes()).padStart(2, '0');
        dateSubtitle = `${hours}:${mins}`;
      }
    } catch {}
  }

  const subtitle = [customerName, [productInfo, dateSubtitle].filter(Boolean).join(', ')]
    .filter(Boolean)
    .join(' · ');

  const handleSelectPaymentMethod = async (methodId: string) => {
    try {
      setSubmittingMethod(methodId);

      if (methodId === 'QRIS') {
        window.open(`/qris/${invoice.invoice_number}`, '_blank');
        showToast.success('Membuka kode QRIS di tab baru');
        onSuccess(invoice.invoice_number, methodId);
        onClose();
        return;
      }

      const response = await invoiceService.updateInvoiceStatus(
        arenaSlug,
        invoice.invoice_number,
        'PAID',
        methodId
      );

      if (response.success) {
        showToast.success(`Pembayaran via ${methodId} berhasil diterima`);
        onSuccess(invoice.invoice_number, methodId);
        onClose();
      } else {
        throw new Error(response.message || 'Gagal memperbarui status invoice');
      }
    } catch (err: any) {
      console.error('Error receiving payment:', err);
      showToast.error(err.message || 'Gagal menerima pembayaran');
    } finally {
      setSubmittingMethod(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !submittingMethod && onClose()}>
      <DialogContent className="max-w-[440px] p-0 overflow-hidden rounded-3xl border border-[#E0E6ED] bg-white">
        {/* Header */}
        <div className="border-b border-[#E0E6ED] px-6 py-4">
          <DialogTitle className="text-base font-semibold text-[#0E1726]">
            Terima pembayaran
          </DialogTitle>
          <p className="mt-1 text-xs text-gray-500 truncate" title={subtitle}>
            {subtitle}
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Sisa Tagihan Banner */}
          <div className="flex items-center justify-between rounded-xl bg-[#FFF8F5] border border-[#FFE4D6] px-4 py-3.5 mb-5">
            <span className="text-xs sm:text-sm font-medium text-gray-700">
              Sisa tagihan
            </span>
            <span className="text-sm sm:text-base font-semibold text-[#FC480D]">
              {formatRupiah(remainingAmount)}
            </span>
          </div>

          {/* Payment Method Cards */}
          <div className="space-y-3">
            {PAYMENT_METHODS.map((pm) => {
              const isSubmitting = submittingMethod === pm.id;
              return (
                <button
                  key={pm.id}
                  type="button"
                  disabled={!!submittingMethod}
                  onClick={() => handleSelectPaymentMethod(pm.id)}
                  className="flex w-full items-center justify-between rounded-2xl border border-[#E0E6ED] p-4 text-left transition-all hover:border-[#FC480D] hover:bg-[#FFFDFB] disabled:opacity-60 disabled:cursor-not-allowed group"
                >
                  <div className="flex items-center gap-3.5">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#FFF5EE] text-[#FC480D]">
                      {isSubmitting ? (
                        <Icon icon="ph:spinner" className="h-5 w-5 animate-spin" />
                      ) : (
                        <Icon icon={pm.icon} className="h-5 w-5" />
                      )}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-[#0E1726]">
                        {pm.title}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {pm.subtitle}
                      </div>
                    </div>
                  </div>

                  <Icon
                    icon="ph:caret-right-bold"
                    className="h-4 w-4 shrink-0 text-gray-400 transition-colors group-hover:text-[#FC480D]"
                  />
                </button>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReceivePaymentModal;
